import React, { useMemo } from 'react';
import { Link } from './Link';
import { usePageContext } from 'vike-react/usePageContext';
import { Icon } from './icons/Icon';
import type { FooterStaticStrings } from '../utils/footer/buildFooterStaticStrings';
import { useFooterStrings } from '../contexts/FooterStringsContext';

/** Curated blog/article links per locale - labels are locale-specific, not from message bundle. */
const BLOG_LINKS: Record<string, { href: string; label: string }[]> = {
  en: [{ href: '/resources/jobs-in-finland/best-job-websites-finland', label: 'Best Job Websites in Finland' }],
  fi: [{ href: '/tyoelama/tyot-suomessa/parhaat-tyopaikkasivustot-suomessa', label: 'Parhaat työnhakusivustot Suomessa' }],
  sv: [{ href: '/arbetsliv/jobb-i-finland/basta-jobbsajter-finland', label: 'Bästa jobbsajter i Finland' }],
};

function FooterContent({
  locale,
  strings,
}: {
  locale: string;
  strings: FooterStaticStrings;
}) {
  const blogLinks = BLOG_LINKS[locale] ?? BLOG_LINKS['en'];

  return (
    <footer
        className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 -mx-2 md:-mx-4 lg:-mx-8"
        data-footer="true"
        itemScope
        itemType="https://schema.org/WPFooter"
      >
        <div className="px-2 py-2 md:px-4 md:py-2 border-b border-gray-200 dark:border-gray-700">
          <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 text-center italic">
            {strings.aiTranslationDisclaimer}
          </p>
        </div>
        <div className="px-2 py-2 flex flex-col md:flex-row md:items-center md:justify-between md:px-4 md:py-3 gap-y-2 gap-x-4">
          <span className="text-xs text-gray-500 dark:text-gray-400 text-center md:text-left">
            {strings.copyrightBefore}
            <a href="https://www.ashtonspina.com/" className="hover:underline dark:hover:text-gray-300">
              TechTactics Oy
            </a>
            {strings.copyrightAfter}
          </span>
          <ul className="flex flex-row flex-wrap items-center justify-center text-xs text-gray-500 dark:text-gray-400 gap-x-2 md:gap-x-4">
            <li>
              <Link href="/resources" locale={locale} className="hover:underline dark:hover:text-gray-300">
                {strings.resources}
              </Link>
            </li>
            <li>
              <Link href="/business" locale={locale} className="hover:underline dark:hover:text-gray-300">
                {strings.business}
              </Link>
            </li>
            <li>
              <Link href="/privacy" locale={locale} className="hover:underline dark:hover:text-gray-300">
                {strings.privacy}
              </Link>
            </li>
            <li>
              <Link href="/terms" locale={locale} className="hover:underline dark:hover:text-gray-300">
                {strings.terms}
              </Link>
            </li>
          </ul>
        </div>
        <div className="px-4 pt-0 pb-4 md:px-6">
          <div className="flex justify-center">
            <div className="w-full max-w-6xl">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white text-center mb-3">
                {strings.fromTheBlog}
              </h3>
              <ul className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm">
                {blogLinks.map((item) => (
                  <li key={item.href}>
                    <Link href={item.href} locale={locale} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="px-2 pb-2 md:px-4 md:pb-3">
          <ul className="flex flex-row flex-wrap items-center justify-center text-xs text-gray-500 dark:text-gray-400 gap-x-2 md:gap-x-4 mb-2">
            <li>
              <Link href="/" locale="en" className="hover:underline dark:hover:text-gray-300">
                {strings.jobcrawlsEn}
              </Link>
            </li>
            <li>
              <Link href="/" locale="fi" className="hover:underline dark:hover:text-gray-300">
                {strings.jobcrawlsFi}
              </Link>
            </li>
            <li>
              <Link href="/" locale="sv" className="hover:underline dark:hover:text-gray-300">
                {strings.jobcrawlsSv}
              </Link>
            </li>
          </ul>
        </div>
        <div className="px-4 py-4 md:px-6 md:py-6">
          <div className="flex justify-center mb-6">
            <Link
              href="/locations"
              locale={locale}
              className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100"
            >
              {strings.locationsHub}
            </Link>
          </div>
          <div className="flex justify-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 place-items-center w-fit mx-auto">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white text-center">
                  {strings.majorFinnishCities}
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link href="/jobs/location/Helsinki_Finland" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.jobsInHelsinki}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Tampere_Finland" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.jobsInTampere}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Espoo_Finland" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.jobsInEspoo}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Turku_Finland" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.jobsInTurku}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Oulu_Finland" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.jobsInOulu}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Vantaa_Finland" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.jobsInVantaa}
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white text-center">
                  {strings.moreFinnishCities}
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link href="/jobs/location/Vaasa_Finland" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.jobsInVaasa}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Lahti_Finland" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.jobsInLahti}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Kuopio_Finland" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.jobsInKuopio}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Jyvaskyla_Finland" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.jobsInJyvaskyla}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Pori_Finland" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.jobsInPori}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Lappeenranta_Finland" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.jobsInLappeenranta}
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white text-center">
                  {strings.remoteWorkAndCountries}
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link href="/jobs/location/Remote_-_Global" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.remoteGlobalJobs}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Remote_-_Europe" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.remoteEuropeJobs}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Remote_-_MENA" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.remoteMENAJobs}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Europe" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.jobsInEurope}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Finland" locale={locale} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline block text-center">
                      {strings.jobsInFinland}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 pb-4 md:px-6 md:pb-6 border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex justify-center gap-4">
            <button
              onClick={() => window.open('https://www.facebook.com/jobcrawls', '_blank')}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 cursor-pointer text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              aria-label="Facebook"
            >
              <Icon name="facebook" useMaskForColor className="w-6 h-6" />
            </button>
            <button
              onClick={() => window.open('https://fi.linkedin.com/company/jobcrawls', '_blank')}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 cursor-pointer text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              aria-label="LinkedIn"
            >
              <Icon name="linkedin" useMaskForColor className="w-6 h-6" />
            </button>
          </div>
        </div>
      </footer>
  );
}

const MemoizedFooterContent = React.memo(FooterContent);

export interface FooterProps {
  /** Locale for links. When omitted, uses pageContext.locale. */
  locale?: string;
  /** Pre-computed footer strings (SSR). When omitted, uses FooterStringsContext. */
  strings?: FooterStaticStrings;
}

function Footer({ locale: localeProp, strings: stringsProp }: FooterProps = {}) {
  const pageContext = usePageContext();
  const stringsFromContext = useFooterStrings();

  const locale = useMemo(
    () => localeProp ?? (pageContext as any)?.locale ?? 'en',
    [localeProp, (pageContext as any)?.locale]
  );

  const strings = stringsProp ?? stringsFromContext;
  if (!strings) {
    throw new Error(
      'Footer requires strings: pass strings prop (e.g. from SRP view model) or wrap in FooterStringsProvider'
    );
  }

  return <MemoizedFooterContent locale={locale} strings={strings} />;
}

export default Footer;
